import { getIsDeployJs } from '@/services/api';

export type ShopifyGateMode = 'enable' | 'authorize' | 'reauth' | 'retry';

export interface PublishGateView {
  pass: boolean;
  mode: '' | 'shopline' | ShopifyGateMode;
  authorizationUrl: string;
  recheckMessage: string;
  integrationType: number;
}

interface DeployJsPayload {
  integrationType?: number;
  deployJs?: boolean;
  themeEmbedEnabled?: boolean | null;
  hasReadThemes?: boolean | null;
  tokenValid?: boolean | null;
  checkFailed?: boolean | null;
  authorizationUrl?: string | null;
}

type Tx = (key: string, fallback: string) => string;

const STILL_OFF = ['form.install.recheckStillOff', '仍未检测到主题嵌入。请确认是当前正在使用的主题，并且已经点击保存。'];
const STILL_AUTH = ['form.install.recheckStillAuth', '仍未检测到读取主题权限。请先完成授权，再开启主题嵌入。'];
const STILL_REAUTH = ['form.install.recheckStillReauth', '仍未完成重新授权。请先点击下方「重新授权」，前往 Shopify 后台完成安装后再继续发布。'];

function text(tx: Tx, pair: string[]): string {
  return tx(pair[0], pair[1]);
}

export function resolvePublishGate(data: DeployJsPayload | null | undefined, recheck: boolean, tx: Tx): PublishGateView {
  const integrationType = Number(data?.integrationType || 0);
  const base = {
    pass: true,
    mode: '' as PublishGateView['mode'],
    authorizationUrl: '',
    recheckMessage: '',
    integrationType,
  };
  if (integrationType === 11) {
    if (data?.deployJs) {
      return base;
    }
    return { ...base, pass: false, mode: 'shopline' };
  }
  if (integrationType !== 1) {
    return base;
  }
  const authorizationUrl = data?.authorizationUrl || '';
  if (data?.checkFailed === true || !hasShopifyGateFields(data)) {
    return { ...base, pass: false, mode: 'retry' };
  }
  if (data?.tokenValid === false) {
    return {
      ...base,
      pass: false,
      mode: 'reauth',
      authorizationUrl,
      recheckMessage: recheck ? text(tx, STILL_REAUTH) : '',
    };
  }
  if (data?.themeEmbedEnabled === true) {
    return base;
  }
  if (data?.hasReadThemes === true && data?.themeEmbedEnabled === false) {
    return {
      ...base,
      pass: false,
      mode: 'enable',
      recheckMessage: recheck ? text(tx, STILL_OFF) : '',
    };
  }
  return {
    ...base,
    pass: false,
    mode: 'authorize',
    authorizationUrl,
    recheckMessage: recheck ? text(tx, data?.hasReadThemes === true ? STILL_OFF : STILL_AUTH) : '',
  };
}

function hasShopifyGateFields(data: DeployJsPayload | null | undefined): boolean {
  if (!data) {
    return false;
  }
  return data.tokenValid != null || data.hasReadThemes != null || data.themeEmbedEnabled != null || data.checkFailed != null;
}

export async function fetchPublishGate(isShopifyKnown: boolean, recheck: boolean, tx: Tx): Promise<PublishGateView> {
  try {
    const res = await getIsDeployJs({}, false);
    return resolvePublishGate(res?.data?.data, recheck, tx);
  } catch {
    if (!isShopifyKnown) {
      return resolvePublishGate(null, recheck, tx);
    }
    return resolvePublishGate({ integrationType: 1, checkFailed: true }, recheck, tx);
  }
}
